import type { MouseEvent } from 'react';
import { DownOutline } from 'antd-mobile-icons';
import { useTranslation } from 'react-i18next';
import type { Trip } from '@ubus/shared';
import DriverStatusPill from './DriverStatusPill';
import {
  extractPlaceName,
  formatEstimatedArrival,
  formatHistoryHeadline,
  formatPassengerCapacity,
  formatTaskScheduleHeadline,
  formatTripVehicleLabel,
  getDetailPhase,
} from '../utils/tripUi';

interface TaskCardProps {
  trip: Trip;
  onClick: () => void;
  variant?: 'hall' | 'mytask-current' | 'mytask-history';
  onSetOff?: (trip: Trip) => void;
  onArrive?: (trip: Trip) => void;
  /** When true, hall「出发」is disabled (e.g. no shift inspection today). */
  setOffBlocked?: boolean;
  expanded?: boolean;
  onToggleExpand?: (e: MouseEvent) => void;
}

function CapacityBadge({ trip }: { trip: Trip }) {
  return (
    <div className="driver-task-card__capacity">
      <svg className="driver-task-card__capacity-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="6" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M7 17v2M17 17v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="8" cy="11" r="1.2" fill="currentColor" />
        <circle cx="16" cy="11" r="1.2" fill="currentColor" />
      </svg>
      <span>{formatPassengerCapacity(trip)}</span>
    </div>
  );
}

function TripNumberLine({
  trip,
  t,
}: {
  trip: Trip;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  if (!trip.trip_number) return null;
  return (
    <div className="driver-task-card__trip-number">
      {t('task.tripNumber')} {trip.trip_number}
    </div>
  );
}

function hallPlaceTitle(trip: Trip, kind: 'pickup' | 'dropoff', t: ReturnType<typeof useTranslation>['t']): string {
  if (kind === 'pickup' && trip.organizations?.name) {
    return trip.organizations.name;
  }
  return extractPlaceName(kind === 'pickup' ? trip.pickup_address : trip.dropoff_address, t);
}

function RouteBlock({ trip, t }: { trip: Trip; t: ReturnType<typeof useTranslation>['t'] }) {
  const pickupClass = 'driver-route-dot driver-route-dot--blue';
  const dropoffClass = 'driver-route-dot driver-route-dot--green';

  return (
    <div className="driver-route driver-route--card">
      <div className={pickupClass} />
      <div>
        <div className="driver-route-place">{hallPlaceTitle(trip, 'pickup', t)}</div>
        <div className="driver-route-text">{trip.pickup_address}</div>
      </div>
      <div className="driver-route-line" />
      <div />
      <div className={dropoffClass} />
      <div>
        <div className="driver-route-place">{hallPlaceTitle(trip, 'dropoff', t)}</div>
        <div className="driver-route-text">{trip.dropoff_address}</div>
      </div>
    </div>
  );
}

function TaskCard({
  trip,
  onClick,
  variant = 'hall',
  onSetOff,
  onArrive,
  setOffBlocked = false,
  expanded,
  onToggleExpand,
}: TaskCardProps) {
  const { t } = useTranslation();
  const phase = getDetailPhase(trip);

  const stop = (e: MouseEvent) => e.stopPropagation();

  if (variant === 'mytask-current') {
    const showArrive = trip.status === 'in_progress';
    return (
      <div className="driver-card driver-task-card driver-task-card--mytask" onClick={onClick}>
        <div className="driver-task-card__mytask-head">
          <div className="driver-task-card__mytask-head-main">
            <DriverStatusPill status={trip.status} />
            <span className="driver-task-card__eta">
              {t('task.expectedArrival', { time: formatEstimatedArrival(trip) })}
            </span>
          </div>
          <CapacityBadge trip={trip} />
        </div>
        <TripNumberLine trip={trip} t={t} />
        <RouteBlock trip={trip} t={t} />
        {showArrive && (
          <button
            type="button"
            className="driver-task-card__action driver-task-card__action--success"
            onClick={(e) => {
              stop(e);
              onArrive?.(trip);
            }}
          >
            {t('task.arrive')}
          </button>
        )}
      </div>
    );
  }

  if (variant === 'mytask-history') {
    return (
      <div className="driver-card driver-task-card driver-task-card--mytask" onClick={onClick}>
        <div className="driver-task-card__mytask-head">
          <span className="driver-task-card__history-title">{formatHistoryHeadline(trip, t)}</span>
          <DriverStatusPill status={trip.status} />
        </div>
        <TripNumberLine trip={trip} t={t} />
        <RouteBlock trip={trip} t={t} />
      </div>
    );
  }

  const showHallAction = phase !== 'not_today' && phase !== 'in_progress';
  const vehicleRow = Array.isArray(trip.vehicles) ? trip.vehicles[0] : trip.vehicles;
  const vehicleDisplay = vehicleRow?.model
    ? `${formatTripVehicleLabel(trip, t)} · ${vehicleRow.model}`
    : formatTripVehicleLabel(trip, t);

  return (
    <div className="driver-card driver-task-card driver-task-card--hall" onClick={onClick}>
      <div className="driver-task-card__hall-head">
        <div className="driver-task-card__departure-inline">
          <strong>{formatTaskScheduleHeadline(trip, t)}</strong>
          <TripNumberLine trip={trip} t={t} />
        </div>
        <div className="driver-task-card__hall-badges">
          {(trip.status === 'dispatched' || trip.status === 'in_progress') && (
            <DriverStatusPill status={trip.status} />
          )}
          <CapacityBadge trip={trip} />
          {onToggleExpand && (
            <button
              type="button"
              className={`driver-task-card__expand ${expanded ? 'driver-task-card__expand--open' : ''}`}
              aria-expanded={expanded}
              aria-label={expanded ? t('task.collapseDetails') : t('task.expandDetails')}
              onClick={onToggleExpand}
            >
              <DownOutline fontSize={14} />
            </button>
          )}
        </div>
      </div>

      <RouteBlock trip={trip} t={t} />

      {expanded && (
        <div className="driver-task-card__expand-body">
          <div className="driver-task-card__detail-row">
            <span className="driver-task-card__detail-label">{t('common.vehicle')}</span>
            <span>{vehicleDisplay}</span>
          </div>
          {(trip.contact_person ||
            trip.contact_phone ||
            trip.organizations?.contact_person ||
            trip.organizations?.contact_phone) && (
            <div className="driver-task-card__detail-row">
              <span className="driver-task-card__detail-label">{t('common.contact')}</span>
              <span>
                {trip.contact_person?.trim() ||
                  trip.organizations?.contact_person ||
                  '—'}
                {(trip.contact_phone?.trim() || trip.organizations?.contact_phone)
                  ? ` · ${trip.contact_phone?.trim() || trip.organizations?.contact_phone}`
                  : ''}
              </span>
            </div>
          )}
          {trip.special_notes && (
            <div className="driver-task-card__detail-row">
              <span className="driver-task-card__detail-label">{t('common.notes')}</span>
              <span>{trip.special_notes}</span>
            </div>
          )}
          {(trip.attachment_urls?.length ?? 0) > 0 && (
            <div className="driver-task-card__detail-row">
              <span className="driver-task-card__detail-label">{t('common.photos')}</span>
              <span>{t('task.photosAttached', { count: trip.attachment_urls!.length })}</span>
            </div>
          )}
        </div>
      )}

      {showHallAction && (
        <button
          type="button"
          className={`driver-task-card__action ${
            phase === 'ready' && !setOffBlocked
              ? 'driver-task-card__action--primary'
              : 'driver-task-card__action--disabled'
          }`}
          disabled={phase !== 'ready' || setOffBlocked}
          onClick={(e) => {
            stop(e);
            if (phase === 'ready' && !setOffBlocked) onSetOff?.(trip);
          }}
        >
          {phase !== 'ready'
            ? t('task.notYetTime')
            : setOffBlocked
              ? t('task.setOffNeedsInspection')
              : t('task.setOff')}
        </button>
      )}

      {phase === 'in_progress' && (
        <button type="button" className="driver-task-card__action driver-task-card__action--primary" onClick={onClick}>
          {t('task.viewInProgress')}
        </button>
      )}
    </div>
  );
}

export default TaskCard;
